/* eslint @typescript-eslint/no-explicit-any: 0 */
import { MAX_INPUT } from '@/config/baseYup';
import cx from 'classnames';
import { isNaN } from 'lodash';
import { InputText } from 'primereact/inputtext';
import { Skeleton } from 'primereact/skeleton';
import React, {
  useState,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';
import { useController, useFormContext } from 'react-hook-form';

type Props = {
  id?: string;
  name: string;
  label?: string;
  defaultValue?: number | string | null;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  requiredFlag?: boolean;
  fullWidthFlag?: boolean;
  wrapClass?: string;
  labelClass?: string;
  type?: 'text' | 'email' | 'tel' | 'password' | 'hidden' | 'number';
  value?: number | null | string;
  disabled?: boolean;
  isLoading?: boolean;
  inputStyle?: React.CSSProperties;
  min?: number;
  max?: number;
  locale?: string;
  mode?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  suffix?: string;
  prefix?: string;
  errorMessage?: string;
  maxLength?: number | undefined;
  isHideValue?: boolean;
  isNumber?: boolean;
  preventStartZero?: boolean;
  prefixIcon?: React.ReactNode;
  preventShowErrorMessage?: boolean;
  showErrorWhileFocused?: boolean;
  convertComma?: boolean;
};

export const InputTextCustomForm: React.FC<Props> = ({
  name,
  label,
  placeholder,
  className,
  requiredFlag,
  fullWidthFlag,
  wrapClass,
  labelClass,
  disabled,
  inputStyle,
  min,
  value = null,
  type = 'text',
  isLoading,
  onChange,
  onBlur,
  onKeyDown,
  errorMessage,
  maxLength = MAX_INPUT,
  id,
  isHideValue,
  isNumber,
  preventStartZero,
  prefixIcon,
  preventShowErrorMessage,
  showErrorWhileFocused = false,
  convertComma,
}) => {
  const [changeType, setChangeType] = useState(isHideValue);
  const [isFocused, setIsFocused] = useState(false);

  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { field } = useController({
    control,
    name,
  });

  const getFieldError = (allErrors: any, fieldName: string) => {
    if (!allErrors) return undefined;

    const pathError = fieldName.split('.').reduce((acc: any, key: string) => {
      if (acc && typeof acc === 'object') {
        return acc[key];
      }
      return undefined;
    }, allErrors);

    if (pathError) return pathError;

    if (fieldName.startsWith('terms.') && allErrors.terms && allErrors.terms.message) {
      return allErrors.terms;
    }

    return undefined;
  };

  const fieldError = getFieldError(errors, name);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;
  
    if (isNumber) {
      value = value.replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0));
      value = value.replace(/[^0-9]/g, '');
      if (preventStartZero) {
        value = value.replace(/^0+/, '');
      }
    }
  
    if (!maxLength || value.length <= maxLength) {
      if (field.value !== value) {
        field.onChange(value);
      }
      onChange?.(e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') &&
      isNumber
    ) {
      e.preventDefault();
    }
    onKeyDown?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (isNumber && convertComma && field.value) {
      const rawNumber = Number(String(field.value).replace(/,/g, ""));
  
      if (!isNaN(rawNumber)) {
        const formattedValue = rawNumber.toLocaleString("ja-JP");
  
        if (String(field.value) !== formattedValue) {
          field.onChange(formattedValue);
        }
      }
    }
    onBlur?.(e);
  };
  
  const handleFocus = () => {
    setIsFocused(true);
  
    if (isNumber && convertComma) {
      const rawValue = String(field.value);
      if (rawValue.includes(",")) {
        field.onChange(rawValue.replace(/,/g, ""));
      }
    }
  };
  

  const formattedValue = (val: string | number | null) => {
    if (!val) return "";
  
    if (isNumber && convertComma) {
      const numericValue = String(val).replace(/,/g, "");
  
      return isFocused ? numericValue : Number(numericValue).toLocaleString("ja-JP");
    }
  
    return String(val);
  };

  return (
    <>
      <div className={cx(wrapClass, 'input')}>
        {label && (
          <div className={cx('label-custom', labelClass)}>
            <label>{label}</label>
            {requiredFlag && (
              <span className={cx('p-error', 'required')}> *</span>
            )}
          </div>
        )}
        {isLoading ? (
          <Skeleton />
        ) : (
          <div className="relative">
            <InputText
              autoComplete="new-password"
              id={id ?? field?.name ?? ''}
              placeholder={placeholder}
              className={cx('input-custom', className, {
                ['p-invalid']: !!fieldError,
                ['w-full']: fullWidthFlag,
              })}
              style={inputStyle}
              {...field}
              name={name}
              value={formattedValue(value ?? field.value)}
              type={changeType ? 'password' : type}
              disabled={disabled}
              min={min}
              onChange={handleChange}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onKeyDown={handleKeyDown}
              maxLength={maxLength}
            />

            {isHideValue && (
              <span
                onClick={() => setChangeType(!changeType)}
                className={cx(
                  'cursor-pointer absolute right-[10px] top-[15px] pi',
                  { 'pi-eye-slash': changeType, 'pi-eye': !changeType },
                )}
              ></span>
            )}

            {prefixIcon}
          </div>
        )}

        {!!fieldError &&
          (fieldError?.message || errorMessage) &&
          !preventShowErrorMessage &&
          (showErrorWhileFocused || !isFocused) && (
          <div
            className={cx('error-message', 'p-error mt-2', {
              ['is-label']: label,
            })}
          >
            {fieldError?.message?.toString() || errorMessage}
          </div>
        )}
      </div>
    </>
  );
};
